import React from 'react';
import { motion } from 'framer-motion';
import { Users, Headset, Network, Zap, Clock } from 'lucide-react';
import { CtaButton } from './CtaButton';
const perks = [{
  icon: Headset,
  title: 'Soporte en vivo DIARIO',
  desc: 'Resolvé tus dudas, inseguridades y desafíos en tiempo real, todos los días.'
}, {
  icon: Network,
  title: 'Red de contactos & networking',
  desc: 'Creadoras UGC, marketers, profesionales y dueñas de marcas conectando entre sí.'
}, {
  icon: Zap,
  title: 'Soluciones casi inmediatas',
  desc: 'Ante cualquier situación, alguien de la comunidad ya pasó por ahí y te ayuda.'
}];
export function CommunitySection() {
  return <section className="bg-black py-20 px-6 flex flex-col items-center text-center w-full relative overflow-hidden" data-id="element-63">
      {/* Ambient neon glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-[#D98BAF]/15 blur-[140px] rounded-full pointer-events-none" data-id="element-64" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-brand-lavender/10 blur-[120px] rounded-full pointer-events-none" data-id="element-65" />

      <div className="max-w-6xl mx-auto flex flex-col items-center relative z-10 w-full" data-id="element-66">
        {/* Urgency eyebrow */}
        <div className="inline-flex items-center gap-2 bg-[#D98BAF]/15 border border-[#D98BAF]/40 text-[#D98BAF] font-bold text-[11px] md:text-sm uppercase tracking-[0.2em] px-5 py-2 rounded-full mb-8 backdrop-blur-sm" data-id="element-67">
          <Clock size={15} strokeWidth={2.5} data-id="element-68" />
          Los cupos se cierran — no te quedes afuera
        </div>

        {/* Neon Title */}
        <h2 className="neon-title font-black text-[30px] md:text-[58px] leading-[1.05] tracking-tight uppercase mb-5 max-w-4xl" style={{
        fontFamily: "'Inter', sans-serif",
        fontWeight: 900
      }} data-id="element-69">
          Sumate a la comunidad de creadoras UGC más activa de Argentina
        </h2>

        <p className="text-white/70 font-medium text-base md:text-xl max-w-2xl leading-relaxed mb-12" data-id="element-70">
          Creadoras de contenido UGC, marketers, profesionales y dueñas de
          marcas, todas conectadas en un mismo lugar. Una red de contactos que,
          por sí sola,{' '}
          <strong className="text-[#D98BAF] font-bold" data-id="element-71">ya es invaluable</strong>
          ... y vos merecés formar parte de ella. 💜
        </p>

        {/* Community image + perks */}
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-14" data-id="element-72">
          {/* Image */}
          <div className="relative flex justify-center" data-id="element-73">
            <div className="absolute -inset-3 bg-gradient-to-tr from-[#D98BAF]/60 via-brand-lavender/40 to-[#D98BAF]/60 rounded-[2.5rem] blur-lg opacity-70" data-id="element-74" />
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            duration: 0.6
          }} className="relative z-10 rounded-[2rem] overflow-hidden border border-[#D98BAF]/30 shadow-[0_0_45px_rgba(217,139,175,0.35)] bg-[#0d0d12]" data-id="element-75">
              <img src="https://cdn.magicpatterns.com/uploads/6D5QvjbU9N3TeDWBhnLCnC/WhatsApp_Image_2026-06-24_at_00.05.53.jpg" alt="Comunidad activa Fórmula UGC con más de 506 creadoras" className="w-full h-auto block max-w-[360px] mx-auto" data-id="element-76" />
            </motion.div>
            {/* Members badge */}
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-[#D98BAF] text-brand-navy font-black text-sm md:text-base px-6 py-2.5 rounded-full shadow-[0_0_25px_rgba(217,139,175,0.6)] whitespace-nowrap" data-id="element-77">
              <Users size={18} strokeWidth={2.5} data-id="element-78" />
              +506 creadoras activas
            </div>
          </div>

          {/* Perks */}
          <div className="flex flex-col gap-5 w-full text-left mt-6 lg:mt-0" data-id="element-79">
            {perks.map(({
            icon: Icon,
            title,
            desc
          }, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            amount: 0.4
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="flex items-start gap-4 bg-white/[0.04] border border-white/10 rounded-2xl p-5 md:p-6 backdrop-blur-sm hover:border-[#D98BAF]/40 transition-colors" data-id="element-80">
                <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-b from-[#D98BAF] to-brand-pinkDark flex items-center justify-center shadow-[0_0_18px_rgba(217,139,175,0.4)]" data-id="element-81">
                  <Icon size={22} strokeWidth={2.5} className="text-white" data-id="element-82" />
                </div>
                <div data-id="element-83">
                  <h3 className="text-white font-bold text-lg md:text-xl mb-1 leading-snug" data-id="element-84">
                    {title}
                  </h3>
                  <p className="text-white/60 text-sm md:text-base leading-relaxed" data-id="element-85">
                    {desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* Urgency close + CTA */}
        <p className="text-white font-semibold text-lg md:text-2xl max-w-3xl leading-relaxed mb-8" data-id="element-86">
          Cada día que esperás es un día sin esta red trabajando para vos.{' '}
          <span className="text-[#D98BAF] font-black" data-id="element-87">
            Asegurá tu lugar antes de que se cierren los cupos.
          </span>
        </p>

        <div className="w-full max-w-md md:scale-110 md:my-2" data-id="element-88">
          <CtaButton variant="gold" data-id="element-89">¡QUIERO ENTRAR A LA COMUNIDAD!</CtaButton>
        </div>
      </div>
    </section>;
}